import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import ProjectCard from '../components/ProjectCard';
import QRCode from 'qrcode.react';
import { useIsAuthenticated } from '@azure/msal-react';

interface LabDayType {
  id: number;
  slug: string;
  title: string;
  description?: string;
  status: 'submission' | 'voting' | 'closed';
  created_at: string;
  projects?: ProjectType[];
}

interface ProjectType {
  id: number;
  lab_day_id: number;
  title: string;
  description: string;
  team_members: string[];
  created_at: string;
  nominatedCategories?: string[];
  voteCounts?: {
    Innovation: number;
    Execution: number;
  };
}

interface NewProjectType {
  title: string;
  description: string;
  team_members: string;
}

const LabDay: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const [labDay, setLabDay] = useState<LabDayType | null>(null);
  const [projects, setProjects] = useState<ProjectType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showQR, setShowQR] = useState(false);
  const [userNominations, setUserNominations] = useState<Array<{project_id: number, category: string}>>([]);
  const [userProjects, setUserProjects] = useState<number[]>([]);
  const [newProject, setNewProject] = useState<NewProjectType>({
    title: '',
    description: '',
    team_members: ''
  });

  // Function to fetch vote counts for all projects
  const fetchVoteCounts = async () => {
    if (!slug || labDay?.status !== 'closed') return;

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const updatedProjects = await Promise.all(
        projects.map(async (project) => {
          const response = await axios.get(`${apiUrl}/api/lab-days/${slug}/projects/${project.id}/votes`);
          return {
            ...project,
            voteCounts: {
              Innovation: response.data.Innovation || 0,
              Execution: response.data.Execution || 0
            }
          };
        })
      );
      setProjects(updatedProjects);
    } catch (error) {
      console.error('Error fetching vote counts:', error);
    }
  };

  // Function to fetch user's nominations
  const fetchUserNominations = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await axios.get(`${apiUrl}/api/lab-days/${slug}/nominations`);
      setUserNominations(response.data);
    } catch (error) {
      console.error('Error fetching user nominations:', error);
      // Don't show error to user as this is not critical
    }
  };

  // Function to fetch user's projects
  const fetchUserProjects = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await axios.get(`${apiUrl}/api/lab-days/${slug}/user-projects`);
      setUserProjects(response.data);
    } catch (error) {
      console.error('Error fetching user projects:', error);
      // Don't show error to user as this is not critical
    }
  };

  useEffect(() => {
    fetchLabDay();

    // Polling mechanism to check for lab day status changes
    const pollInterval = setInterval(async () => {
      try {
        // Only poll if we have a slug
        if (slug) {
          if (import.meta.env.DEV) {
            console.debug('Polling for lab day status');
          }
          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
          const response = await axios.get(`${apiUrl}/api/lab-days/${slug}`);
          
          // Store current status for comparison
          const currentStatus = labDay?.status;
          
          // If status changed to voting and we're not on the voting page, redirect
          if (response.data.status === 'voting' && currentStatus !== 'voting') {
            console.log('Polling detected voting started');
            toast.info('Voting has started!');
            navigate(`/lab/${slug}/vote`);
          }
          // If status changed to closed and we weren't showing that, refresh
          else if (response.data.status === 'closed' && currentStatus === 'voting') {
            console.log('Polling detected voting ended');
            toast.info('Voting has ended!');
            setLabDay(response.data);
            setProjects(response.data.projects || []);
            // Fetch vote counts when voting ends
            fetchVoteCounts();
          }
          // If status is already closed, just update the data without notification
          else if (response.data.status === 'closed' && currentStatus === 'closed') {
            setLabDay(response.data);
            setProjects(response.data.projects || []);
          }
        }
      } catch (error) {
        console.error('Error polling lab day status:', error);
      }
    }, 5000); // Poll every 5 seconds

    // Fetch user nominations and projects initially and then periodically
    fetchUserNominations();
    fetchUserProjects();
    const nominationsInterval = setInterval(fetchUserNominations, 10000); // Every 10 seconds
    const projectsInterval = setInterval(fetchUserProjects, 10000); // Every 10 seconds

    // Only clean up when component unmounts or slug changes
    return () => {
      if (import.meta.env.DEV) {
        console.debug('Clearing polling interval');
      }
      clearInterval(pollInterval);
      clearInterval(nominationsInterval);
      clearInterval(projectsInterval);
    };
    
    // Only re-run when slug or navigate function changes
    // Explicitly NOT including labDay in dependencies to prevent frequent re-runs
  }, [slug, navigate]);

  useEffect(() => {
    if (labDay?.status === 'closed' && projects.length > 0) {
      fetchVoteCounts();
    }
    // eslint-disable-next-line
  }, [labDay?.status, projects.length]);

  const fetchLabDay = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await axios.get(`${apiUrl}/api/lab-days/${slug}`);
      
      // Check if voting is already in progress and redirect immediately if needed
      if (response.data.status === 'voting') {
        console.log('Voting is already in progress, redirecting to voting page');
        setIsLoading(true); // Keep loading state while redirecting
        toast.info('Voting in progress! Redirecting to voting page...');
        setTimeout(() => {
          navigate(`/lab/${slug}/vote`);
        }, 500); // Small delay for toast to show
        return; // Stop further processing
      }
      
      setLabDay(response.data);
      setProjects(response.data.projects || []);
    } catch (error) {
      toast.error('Failed to load Lab Day data');
      console.error('Error fetching Lab Day:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitProject = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const response = await axios.post(`${apiUrl}/api/lab-days/${slug}/projects`, newProject);
      setProjects([...projects, response.data]);
      setNewProject({ title: '', description: '', team_members: '' });
      fetchUserProjects(); // Refresh user projects list
      toast.success('Project submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit project');
      console.error('Error submitting project:', error);
    }
  };

  const handleNominate = async (projectId: number, category: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      await axios.post(`${apiUrl}/api/lab-days/${slug}/projects/${projectId}/nominate`, {
        category
      });
      fetchLabDay(); // Refresh to get updated nominations
      fetchUserNominations(); // Refresh user nominations
      toast.success(`Project nominated for ${category}`);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to nominate project';
      
      // If user has already nominated this specific project, show as info instead of error
      if (errorMessage.includes('already nominated this project')) {
        toast.info(`You have already nominated this project for ${category}`);
        // Update the UI to reflect current state
        fetchLabDay();
        fetchUserNominations();
      } else {
        toast.error(errorMessage);
      }
      console.error('Error nominating project:', error);
    }
  };

  const handleRemoveNomination = async (projectId: number, category: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      await axios.delete(`${apiUrl}/api/lab-days/${slug}/projects/${projectId}/nominate`, {
        data: { category }
      });
      fetchLabDay(); // Refresh to get updated nominations
      fetchUserNominations(); // Refresh user nominations
      toast.success(`Nomination for ${category} removed`);
    } catch (error) {
      toast.error('Failed to remove nomination');
      console.error('Error removing nomination:', error);
    }
  };

  const startVoting = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      await axios.post(`${apiUrl}/api/lab-days/${slug}/start-voting`);
      navigate(`/lab/${slug}/vote`);
      toast.success('Voting phase started!');
    } catch (error) {
      toast.error('Failed to start voting phase');
      console.error('Error starting voting:', error);
    }
  };

  const stopVoting = async () => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      await axios.post(`${apiUrl}/api/lab-days/${slug}/end-voting`);
      toast.success('Voting phase ended!');
      // Refresh the lab day data to show updated status
      fetchLabDay();
    } catch (error) {
      toast.error('Failed to stop voting phase');
      console.error('Error stopping voting:', error);
    }
  };

  // Function to get winners for each category (can be multiple)
  const getWinners = () => {
    if (!projects.length) return { Innovation: [], Execution: [] };

    // Max votes pro Kategorie bestimmen
    const maxInnovation = Math.max(...projects.map(p => p.voteCounts?.Innovation || 0));
    const maxExecution = Math.max(...projects.map(p => p.voteCounts?.Execution || 0));

    return {
      Innovation: projects.filter(p => (p.voteCounts?.Innovation || 0) === maxInnovation && maxInnovation > 0),
      Execution: projects.filter(p => (p.voteCounts?.Execution || 0) === maxExecution && maxExecution > 0)
    };
  };

  const winners = getWinners();

  const handleProjectUpdate = () => {
    // Refresh data after project update
    fetchLabDay();
    fetchUserProjects();
  };

  if (isLoading) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shopware-blue mb-4"></div>
        <p className="text-shopware-dark-blue text-xl">Loading Lab Day data...</p>
      </div>
    );
  }

  if (!labDay) {
    return (
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-shopware-dark-blue">Lab Day not found</h1>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center gap-3 mb-2">
        <img src="/open-logo.png" alt="Open Logo" className="h-10 w-10" />
        <h1 className="text-3xl font-bold text-shopware-dark-blue flex items-center gap-2">
          {labDay.title}
          <span className={`text-base align-middle font-normal ${
            labDay.status === 'submission' ? 'text-green-500' :
            labDay.status === 'voting' ? 'text-orange-500' :
            'text-red-500'
          }`}>
            {labDay.status === 'submission' ? 'open for submissions' :
             labDay.status === 'voting' ? 'voting in progress' :
             'closed'}
          </span>
        </h1>
      </div>
      <p className="text-m text-gray-500 mt-1 mb-8">
        {new Date(labDay.created_at).toLocaleDateString()}
      </p>
      <div className="flex justify-between items-center mb-8">
        <div> </div>
        <div className="flex gap-4">
          <button
            onClick={() => setShowQR(true)}
            className="bg-shopware-light-blue text-shopware-dark-blue px-4 py-2 rounded-xl hover:bg-shopware-blue hover:text-white transition-all"
          >
            Show QR Code
          </button>
          {isAuthenticated && labDay.status === 'submission' && (
            <button
              onClick={startVoting}
              className="bg-shopware-blue text-white px-4 py-2 rounded-xl hover:bg-shopware-dark-blue transition-all"
            >
              Start Voting
            </button>
          )}
          {isAuthenticated && labDay.status === 'voting' && (
            <button
              onClick={stopVoting}
              className="bg-red-500 text-white px-4 py-2 rounded-xl hover:bg-red-600 transition-all"
            >
              Stop Voting
            </button>
          )}
        </div>
      </div>

      {/* QR Code Modal */}
      {showQR && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full">
            <h3 className="text-xl font-semibold text-shopware-dark-blue mb-4">
              Share Lab Day
            </h3>
            <div className="flex justify-center mb-4">
              <QRCode
                value={`${window.location.origin}/lab/${slug}`}
                size={200}
                level="H"
              />
            </div>
            <p className="text-sm text-gray-600 mb-4 text-center">
              Scan this QR code to access the Lab Day
            </p>
            <button
              onClick={() => setShowQR(false)}
              className="w-full bg-shopware-blue text-white py-2 rounded-xl hover:bg-shopware-dark-blue transition-all"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Project Submission Form */}
      {labDay.status === 'submission' && (
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-2xl font-semibold text-shopware-dark-blue mb-4">
            Submit a Project
          </h2>
          <form onSubmit={handleSubmitProject}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project Title
                </label>
                <input
                  type="text"
                  value={newProject.title}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => 
                    setNewProject({ ...newProject, title: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-shopware-blue focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newProject.description}
                  onChange={(e: ChangeEvent<HTMLTextAreaElement>) => 
                    setNewProject({ ...newProject, description: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-shopware-blue focus:border-transparent"
                  rows={4}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Team Members (comma-separated)
                </label>
                <input
                  type="text"
                  value={newProject.team_members}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => 
                    setNewProject({ ...newProject, team_members: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-shopware-blue focus:border-transparent"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-shopware-blue text-white py-2 rounded-xl hover:bg-shopware-dark-blue transition-all"
              >
                Submit Project
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Projects Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {projects.map((project, index) => (
          <ProjectCard
            key={index}
            project={project}
            onNominate={handleNominate}
            onRemoveNomination={handleRemoveNomination}
            isVotingPhase={false}
            labDayStatus={labDay?.status || 'submission'}
            slug={slug || ''}
            userNominations={userNominations}
            userProjects={userProjects}
            onProjectUpdate={handleProjectUpdate}
            isWinner={{
              Innovation: winners.Innovation.some(p => p.id === project.id),
              Execution: winners.Execution.some(p => p.id === project.id)
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default LabDay; 